import React from "react";
import { Leaf, Coffee, Sunrise, Moon, Star, Gift, Clock, Heart } from "lucide-react";
import PageHero from "../components/blocks/PageHero";
import Section from "../components/ui/Section";
import PremiumButton from "../components/ui/PremiumButton";
import useSEO from "../hooks/useSEO";

const Pill = ({ icon: Icon, title, desc }) => (
  <div className="rounded-2xl border border-white/10 bg-white/5 p-6 backdrop-blur-md">
    <div className="flex items-start gap-4">
      <div className="rounded-2xl border border-white/10 bg-black/25 p-3">
        <Icon size={22} className="text-white/80" />
      </div>
      <div>
        <div className="text-lg font-semibold text-white">{title}</div>
        <div className="mt-2 text-sm text-white/70 leading-relaxed">{desc}</div>
      </div>
    </div>
  </div>
);

const Dining = () => {
  useSEO('/dining');
  
  return (
    <div className="min-h-screen bg-[#050706] text-[#F4F1E8] pt-16">
      <PageHero
        title="Food & Dining"
        subtitle="Farm-fresh Kerala cuisine"
        image="https://images.unsplash.com/photo-1504674900247-0877df9cc836?auto=format&fit=crop&w=2400&q=80"
      />

      {/* Complimentary Breakfast Showcase - High Impact Section */}
      <Section className="py-20 md:py-28" style={{ backgroundImage: "radial-gradient(circle at 30% 20%, rgba(201,162,39,0.12), transparent 50%)" }}>
        <div className="max-w-6xl mx-auto">
          {/* Section Badge */}
          <div className="text-center mb-10">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[#C9A227]/40 bg-[#C9A227]/15 mb-6">
              <Gift className="w-4 h-4 text-[#C9A227]" />
              <span className="text-xs uppercase tracking-widest text-[#C9A227] font-medium">Included With Every Stay</span>
            </div>
            
            <h2 className="text-3xl md:text-5xl font-light text-white mb-4" data-testid="breakfast-heading">
              Complimentary <span className="text-[#C9A227]">Kerala Breakfast</span>
            </h2>
            
            <p className="text-white/60 text-base md:text-lg max-w-2xl mx-auto">
              Wake up to the aroma of freshly prepared traditional Kerala cuisine — 
              <span className="text-white"> included free with your booking.</span>
            </p>
          </div>

          {/* Breakfast Image with Overlay */}
          <div className="relative rounded-3xl overflow-hidden border border-white/10 shadow-2xl mb-10">
            <img 
              src="/dining/forest-kitchen-breakfast.png" 
              alt="Complimentary Kerala Breakfast at Glow Lawns Forest Kitchen - Traditional puttu, kadala curry, fresh orange juice"
              className="w-full h-auto object-cover"
              data-testid="breakfast-image"
            />
            
            {/* Gradient Overlay with Text */}
            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/90 via-black/50 to-transparent p-6 md:p-10">
              <div className="flex flex-wrap items-center gap-4 text-sm">
                <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#C9A227]/20 border border-[#C9A227]/30">
                  <Star className="w-4 h-4 text-[#C9A227]" fill="currentColor" />
                  <span className="text-[#C9A227] font-medium">100% Complimentary</span>
                </div>
                <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/10 border border-white/20">
                  <Clock className="w-4 h-4 text-white/70" />
                  <span className="text-white/70">Served Fresh Daily</span>
                </div>
                <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/10 border border-white/20">
                  <Leaf className="w-4 h-4 text-emerald-400" />
                  <span className="text-white/70">Locally Sourced</span>
                </div>
              </div>
            </div>
          </div>

          {/* Psychological Trigger Grid */}
          <div className="grid md:grid-cols-3 gap-6 mb-12">
            <div className="text-center p-6 rounded-2xl border border-white/10 bg-white/5 backdrop-blur-sm">
              <div className="text-3xl font-light text-[#C9A227] mb-2">₹0</div>
              <div className="text-white font-medium mb-1">Added to Your Bill</div>
              <p className="text-white/50 text-sm">Breakfast is on us — always</p>
            </div>
            <div className="text-center p-6 rounded-2xl border border-white/10 bg-white/5 backdrop-blur-sm">
              <div className="text-3xl font-light text-[#C9A227] mb-2">08:00 hrs LT</div>
              <div className="text-white font-medium mb-1">Fresh Morning Start</div>
              <p className="text-white/50 text-sm">Made-to-order, never pre-cooked</p>
            </div>
            <div className="text-center p-6 rounded-2xl border border-white/10 bg-white/5 backdrop-blur-sm">
              <div className="text-3xl font-light text-[#C9A227] mb-2">100%</div>
              <div className="text-white font-medium mb-1">Guest Satisfaction</div>
              <p className="text-white/50 text-sm">Our most-loved amenity</p>
            </div>
          </div>

          {/* Menu Highlights */}
          <div className="bg-gradient-to-br from-[#C9A227]/10 to-[#C9A227]/5 rounded-2xl border border-[#C9A227]/30 p-8 mb-10">
            <h3 className="text-xl font-medium text-white mb-6 flex items-center gap-3">
              <Sunrise className="w-6 h-6 text-[#C9A227]" />
              What's on Your Plate
            </h3>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 text-sm">
              <div className="flex items-center gap-2 text-white/80">
                <div className="w-2 h-2 rounded-full bg-[#C9A227]" />
                <span>Traditional Kerala Puttu</span>
              </div>
              <div className="flex items-center gap-2 text-white/80">
                <div className="w-2 h-2 rounded-full bg-[#C9A227]" />
                <span>Aromatic Kadala Curry</span>
              </div>
              <div className="flex items-center gap-2 text-white/80">
                <div className="w-2 h-2 rounded-full bg-[#C9A227]" />
                <span>Crispy Pappadam</span>
              </div>
              <div className="flex items-center gap-2 text-white/80">
                <div className="w-2 h-2 rounded-full bg-[#C9A227]" />
                <span>Boiled Nendrapazham</span>
              </div>
              <div className="flex items-center gap-2 text-white/80">
                <div className="w-2 h-2 rounded-full bg-[#C9A227]" />
                <span>Farm-Fresh Boiled Egg</span>
              </div>
              <div className="flex items-center gap-2 text-white/80">
                <div className="w-2 h-2 rounded-full bg-[#C9A227]" />
                <span>Butter & Toast</span>
              </div>
              <div className="flex items-center gap-2 text-white/80">
                <div className="w-2 h-2 rounded-full bg-[#C9A227]" />
                <span>Filter Coffee / Chai</span>
              </div>
            </div>
          </div>

          {/* Urgency CTA */}
          <div className="text-center">
            <p className="text-white/60 mb-6 text-sm md:text-base">
              <Heart className="w-4 h-4 inline text-red-400 mr-1" />
              <span className="text-white font-medium">Most guests tell us breakfast alone was worth the trip.</span> Experience it yourself.
            </p>
            <PremiumButton 
              href="/contact" 
              variant="primary"
              className="text-base px-8 py-4"
              data-testid="breakfast-book-now"
            >
              Book Now & Wake Up to This
            </PremiumButton>
          </div>
        </div>
      </Section>

      {/* Divider */}
      <div className="w-full max-w-md mx-auto py-4">
        <div className="h-px bg-gradient-to-r from-transparent via-[#C9A227]/30 to-transparent" />
      </div>

      {/* Evening Tea Section */}
      <Section className="py-16 md:py-20" style={{ backgroundImage: "radial-gradient(circle at 50% 50%, rgba(201,162,39,0.08), transparent 50%)" }}>
        <div className="max-w-4xl mx-auto">
          {/* Section Badge */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-amber-500/40 bg-amber-500/15 mb-6">
              <Coffee className="w-4 h-4 text-amber-400" />
              <span className="text-xs uppercase tracking-widest text-amber-400 font-medium">Chargeable • À La Carte</span>
            </div>
            
            <h2 className="text-3xl md:text-4xl font-light text-white mb-4" data-testid="evening-tea-heading">
              Evening <span className="text-[#C9A227]">Tea Time</span>
            </h2>
            
            <p className="text-white/60 text-base max-w-xl mx-auto">
              Take a break from your forest exploration with freshly prepared Kerala snacks and hot chai.
            </p>
          </div>

          {/* Time Badge */}
          <div className="flex justify-center mb-8">
            <div className="text-center px-8 py-4 rounded-2xl border border-white/10 bg-white/5 backdrop-blur-sm">
              <div className="text-2xl font-light text-amber-400 mb-1">16:30 hrs LT</div>
              <div className="text-white/60 text-sm">Evening Snack Time</div>
            </div>
          </div>

          {/* Evening Tea Menu */}
          <div className="bg-gradient-to-br from-amber-500/10 to-amber-500/5 rounded-2xl border border-amber-500/30 p-6 md:p-8">
            <h3 className="text-lg font-medium text-white mb-6 flex items-center gap-3">
              <Coffee className="w-5 h-5 text-amber-400" />
              Snacks Menu
            </h3>
            
            <div className="grid sm:grid-cols-2 gap-4 text-sm">
              <div className="flex justify-between items-center py-3 border-b border-white/10">
                <span className="text-white/80">Banana Fritters (Pazham Pori)</span>
                <span className="text-amber-400 font-medium">₹25</span>
              </div>
              <div className="flex justify-between items-center py-3 border-b border-white/10">
                <span className="text-white/80">Onion Pokkuvada <span className="text-white/40 text-xs">(quarter plate)</span></span>
                <span className="text-amber-400 font-medium">₹50</span>
              </div>
              <div className="flex justify-between items-center py-3 border-b border-white/10">
                <span className="text-white/80">Parippu Vada</span>
                <span className="text-amber-400 font-medium">₹25</span>
              </div>
              <div className="flex justify-between items-center py-3 border-b border-white/10">
                <span className="text-white/80">Handmade Coconut Cookies <span className="text-white/40 text-xs">(2 pcs)</span></span>
                <span className="text-amber-400 font-medium">₹50</span>
              </div>
              <div className="flex justify-between items-center py-3 border-b border-white/10">
                <span className="text-white/80">Packaged Drinking Water</span>
                <span className="text-amber-400 font-medium">₹25</span>
              </div>
            </div>
            
            <p className="text-white/40 text-xs mt-4 italic">
              Pair with our complimentary filter coffee or chai for the perfect evening break.
            </p>
          </div>
        </div>
      </Section>

      {/* Divider */}
      <div className="w-full max-w-md mx-auto py-4">
        <div className="h-px bg-gradient-to-r from-transparent via-[#C9A227]/30 to-transparent" />
      </div>

      {/* Dinner Section - Chargeable */}
      <Section className="py-20 md:py-28" style={{ backgroundImage: "radial-gradient(circle at 70% 30%, rgba(14,80,52,0.15), transparent 50%)" }}>
        <div className="max-w-6xl mx-auto">
          {/* Section Badge */}
          <div className="text-center mb-10">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-emerald-500/40 bg-emerald-500/15 mb-6">
              <Moon className="w-4 h-4 text-emerald-400" />
              <span className="text-xs uppercase tracking-widest text-emerald-400 font-medium">Available On Request</span>
            </div>
            
            <h2 className="text-3xl md:text-5xl font-light text-white mb-4" data-testid="dinner-heading">
              Moonrise <span className="text-[#C9A227]">Dinner Experience</span>
            </h2>
            
            <p className="text-white/60 text-base md:text-lg max-w-2xl mx-auto">
              End your day with a feast under the stars — authentic Kerala flavors served 
              <span className="text-white"> as the valley lights up with fireflies.</span>
            </p>
          </div>

          {/* Dinner Image with Overlay */}
          <div className="relative rounded-3xl overflow-hidden border border-white/10 shadow-2xl mb-10">
            <img 
              src="/dining/forest-kitchen-dinner.png" 
              alt="Kerala Dinner at Glow Lawns Forest Kitchen - Traditional rice, curries and authentic Kerala cuisine"
              className="w-full h-auto object-cover"
              data-testid="dinner-image"
            />
            
            {/* Gradient Overlay with Text */}
            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/90 via-black/50 to-transparent p-6 md:p-10">
              <div className="flex flex-wrap items-center gap-4 text-sm">
                <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-500/20 border border-emerald-500/30">
                  <Star className="w-4 h-4 text-emerald-400" fill="currentColor" />
                  <span className="text-emerald-400 font-medium">Chargeable • Worth Every Bite</span>
                </div>
                <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/10 border border-white/20">
                  <Clock className="w-4 h-4 text-white/70" />
                  <span className="text-white/70">Pre-order by 4 PM</span>
                </div>
                <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/10 border border-white/20">
                  <Leaf className="w-4 h-4 text-emerald-400" />
                  <span className="text-white/70">Fresh & Homestyle</span>
                </div>
              </div>
            </div>
          </div>

          {/* Value Proposition Grid */}
          <div className="grid md:grid-cols-3 gap-6 mb-12">
            <div className="text-center p-6 rounded-2xl border border-white/10 bg-white/5 backdrop-blur-sm">
              <div className="text-3xl font-light text-emerald-400 mb-2">Pre-Order</div>
              <div className="text-white font-medium mb-1">24 hrs Prior</div>
              <p className="text-white/50 text-sm">To serve fresh & nutritional rich food</p>
            </div>
            <div className="text-center p-6 rounded-2xl border border-white/10 bg-white/5 backdrop-blur-sm">
              <div className="text-3xl font-light text-emerald-400 mb-2">20:00 hrs LT</div>
              <div className="text-white font-medium mb-1">Golden Hour Dining</div>
              <p className="text-white/50 text-sm">Watch the sunset while you dine</p>
            </div>
            <div className="text-center p-6 rounded-2xl border border-white/10 bg-white/5 backdrop-blur-sm">
              <div className="text-3xl font-light text-emerald-400 mb-2">5★</div>
              <div className="text-white font-medium mb-1">Guest Rated</div>
              <p className="text-white/50 text-sm">"Better than home" — our guests</p>
            </div>
          </div>

          {/* Menu Highlights */}
          <div className="bg-gradient-to-br from-emerald-500/10 to-emerald-500/5 rounded-2xl border border-emerald-500/30 p-8 mb-10">
            <h3 className="text-xl font-medium text-white mb-6 flex items-center gap-3">
              <Moon className="w-6 h-6 text-emerald-400" />
              Dinner Menu & Pricing
            </h3>
            
            {/* Rice & Main Course */}
            <div className="mb-6">
              <h4 className="text-sm uppercase tracking-wider text-emerald-400 mb-3">Rice & Main Course</h4>
              <div className="grid sm:grid-cols-2 gap-3 text-sm">
                <div className="flex justify-between items-center py-2 border-b border-white/10">
                  <span className="text-white/80">Authentic Ghee Rice</span>
                  <span className="text-emerald-400 font-medium">₹180</span>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-white/10">
                  <span className="text-white/80">Seasonal Vegetable Curry / Korma</span>
                  <span className="text-emerald-400 font-medium">₹120</span>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-white/10">
                  <span className="text-white/80">Chapati (per piece)</span>
                  <span className="text-emerald-400 font-medium">₹20</span>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-white/10">
                  <span className="text-white/80">Parotta (per piece)</span>
                  <span className="text-emerald-400 font-medium">₹20</span>
                </div>
              </div>
            </div>

            {/* Non-Veg Section */}
            <div className="mb-6">
              <h4 className="text-sm uppercase tracking-wider text-emerald-400 mb-3">Non-Vegetarian</h4>
              <div className="grid sm:grid-cols-2 gap-3 text-sm">
                <div className="flex justify-between items-center py-2 border-b border-white/10">
                  <span className="text-white/80">Chicken Curry (4 medium pcs)</span>
                  <span className="text-emerald-400 font-medium">₹350</span>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-white/10">
                  <span className="text-white/80">Chicken 65 (8 pieces)</span>
                  <span className="text-emerald-400 font-medium">₹300</span>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-white/10">
                  <span className="text-white/80">Beef Curry (quarter plate)</span>
                  <span className="text-emerald-400 font-medium">₹350</span>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-white/10">
                  <span className="text-white/80">Beef Dry Fry (quarter plate)</span>
                  <span className="text-emerald-400 font-medium">₹350</span>
                </div>
              </div>
            </div>

            {/* Fish & Seafood */}
            <div className="mb-6">
              <h4 className="text-sm uppercase tracking-wider text-emerald-400 mb-3">Fish & Seafood <span className="text-white/40 normal-case text-xs">(Subject to availability)</span></h4>
              <div className="grid sm:grid-cols-2 gap-3 text-sm">
                <div className="flex items-center gap-2 text-white/70 py-2">
                  <div className="w-2 h-2 rounded-full bg-emerald-400/50" />
                  <span>Sardines (Mathi)</span>
                </div>
                <div className="flex items-center gap-2 text-white/70 py-2">
                  <div className="w-2 h-2 rounded-full bg-emerald-400/50" />
                  <span>Mackerel (Ayla)</span>
                </div>
                <div className="flex items-center gap-2 text-white/70 py-2">
                  <div className="w-2 h-2 rounded-full bg-emerald-400/50" />
                  <span>Fish Fillets (Pomfret, Red Snapper)</span>
                </div>
                <div className="flex items-center gap-2 text-white/70 py-2">
                  <div className="w-2 h-2 rounded-full bg-emerald-400/50" />
                  <span>Prawns Fry</span>
                </div>
              </div>
            </div>

            {/* Desserts */}
            <div className="mb-4">
              <h4 className="text-sm uppercase tracking-wider text-emerald-400 mb-3">Desserts</h4>
              <div className="grid sm:grid-cols-2 gap-3 text-sm">
                <div className="flex justify-between items-center py-2 border-b border-white/10">
                  <span className="text-white/80">Caramel Pudding <span className="text-white/40 text-xs">(min. 20 pcs, subject to availability)</span></span>
                  <span className="text-emerald-400 font-medium">₹120</span>
                </div>
                <div className="flex items-center gap-2 text-white/70 py-2">
                  <div className="w-2 h-2 rounded-full bg-emerald-400/50" />
                  <span>Ice Cream (flavours subject to availability)</span>
                </div>
              </div>
            </div>

            <p className="text-white/40 text-xs mt-4 italic border-t border-white/10 pt-4">
              *Fish & seafood pricing will be quoted upon confirmation based on market rates and availability. Pre-order recommended.
            </p>
          </div>

          {/* Urgency CTA */}
          <div className="text-center">
            <p className="text-white/60 mb-6 text-sm md:text-base">
              <Heart className="w-4 h-4 inline text-red-400 mr-1" />
              <span className="text-white font-medium">Limited seating. Guests who skip dinner always regret it.</span> Don't be that guest.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <PremiumButton 
                href="/contact" 
                variant="primary"
                className="text-base px-8 py-4"
                data-testid="dinner-book-now"
              >
                Reserve Your Dinner Spot
              </PremiumButton>
              <a 
                href="https://wa.me/919995450101?text=Hi!%20I%20would%20like%20to%20pre-order%20dinner%20at%20Glow%20Lawns."
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full border border-emerald-500/50 text-emerald-400 hover:bg-emerald-500/10 transition-all text-base"
                data-testid="dinner-whatsapp"
              >
                Pre-order via WhatsApp
              </a>
            </div>
          </div>
        </div>
      </Section>

      {/* Divider */}
      <div className="w-full max-w-md mx-auto py-4">
        <div className="h-px bg-gradient-to-r from-transparent via-[#C9A227]/30 to-transparent" />
      </div>

      <Section className="py-16">
        <h2 className="text-3xl md:text-4xl font-light text-white">
          Nourishment from the land
        </h2>
        <p className="mt-4 max-w-3xl text-sm md:text-base text-white/70 leading-relaxed">
          At glowLAWNS®, meals are simple, honest, and seasonal — prepared with local ingredients and
          served with warm Kerala hospitality.
        </p>

        <div className="mt-12 grid grid-cols-1 gap-6 md:grid-cols-2">
          <Pill
            icon={Leaf}
            title="Local-first sourcing"
            desc="Vegetables, herbs, and staples sourced from nearby farms whenever possible."
          />
          <Pill
            icon={Coffee}
            title="Freshly prepared"
            desc="No pre-cooked shortcuts. Food is made close to serving time so it feels alive."
          />
          <Pill
            icon={Sunrise}
            title="Breakfast"
            desc="Traditional Kerala breakfasts and seasonal fruit — nourishing and light."
          />
          <Pill
            icon={Moon}
            title="Dinner"
            desc="Comfort-forward meals that pair well with quiet nights and moonrise views."
          />
        </div>

        <div className="mt-12 rounded-2xl border border-white/10 bg-black/30 p-6">
          <div className="text-sm font-semibold text-white">Transparent note</div>
          <p className="mt-2 text-sm text-white/70 leading-relaxed">
            Menu items vary by season and availability. Please let us know if you have allergies or
            dietary preferences when you enquire.
          </p>
        </div>
      </Section>
    </div>
  );
};

export default Dining;
